import { Component, OnInit } from '@angular/core';
import { ApiService } from '../_services/api.service';
import { BehaviorSubject } from 'rxjs';
import { ConfigService } from '../_services/config.service';
import { MatDialog } from '@angular/material/dialog';
import { SectionOverviewDialogComponent } from './section-overview/section-overview.component';
import { FlowComponent } from './flow/flow.component';

export interface Section {
  id: number;
  template: string;
  sectionOrder: number;
  questionOrder: number;
  section: string;
  questionTitle: string;
  questionSubtitle: string;
  description: string;
  example: string;
  answer: string;
  answerId: number | null;
  recommendations: string;
}

@Component({
  selector: 'questionnaoire',
  template: '',
  standalone: false
})
export abstract class QuestionnaireComponent implements OnInit {
  sections: Section[] = [];
  selectedSection: Section | null = null;  
  undoStack: string[] = [];
  redoStack: string[] = [];
  saveTimeout: any;
  saveTimeoutInMilliS: number = 1500;
  lastSaveTime: string | null = null;
  animate: boolean = false;
  assessmentName: string = "/";
  assessmentObject: string = "";
  questionnaireAssesmentId = null;
  assessmentData$ = new BehaviorSubject<any>(null);

  recommendationsLoading = false;

  constructor(
    protected api: ApiService,
    public readonly config: ConfigService,
    protected dialog: MatDialog) {}


  ngOnInit(): void {    
    this.loadData();
    this.loadSections();
  }

  abstract getTemplateId(): string;

  loadData(): void {
    this.api.getAssessment().subscribe((assessment) => {
      this.api.getAssessmentQuestionnaires().subscribe((data: any) => {        
        this.assessmentData$.next({ data, assessment });
      });
    });
  }
  
  loadSections(): void {
    this.assessmentData$.subscribe((assessmentData) => {
      if (!assessmentData) {        
        return;
      }  
      
      const { data, assessment } = assessmentData;  
      const templateId = this.getTemplateId();     
  
      let section = data.find((s: any) =>
        s.template?.name?.toLowerCase().trim().includes(templateId.toLowerCase().trim())
      );
      
      if (!section) {
        console.error("No matching section found for templateId:", templateId);
        return;
      }  
      this.questionnaireAssesmentId = section.id;
      this.assessmentName = assessment.name;
      this.assessmentObject = assessment.object;  
      const answers = section.template.answers || [];        
      this.sections = section.template.questions.map((q: any) => {
        const matchedAnswer = answers.find((a: any) => a.questionId === q.id);
        return {
          ...q,
          answer: this.getAnswer(q, matchedAnswer, data),
          answerId: matchedAnswer ? matchedAnswer.id : null,
          recommendations: this.getRecommendation(matchedAnswer),
        };
      });  
      if (this.sections.length > 0) {
        this.selectedSection = this.sections[0];
      } else {
        console.warn("No sections available");
      }
    });
  }

  getAnswer(question, matchedAnswer, data): string {    
    const prefix = "Parts of this section may align with the Lean Canvas, here's what you've already written on the topic for your reference (delete this after you've expanded on the answer):\n\n";
    let returnAnswer = matchedAnswer ? matchedAnswer.answerText : '';
    
    if (this.getTemplateId() !== 'CANVAS' && returnAnswer === '' && this.getTemplateId() !== 'TRL') {
        let canvas = data.find((s: any) =>
            s.template?.name?.toLowerCase().trim().includes('canvas')
        );        
        if (!canvas) {
          console.error("No canvas found");
          returnAnswer = '';
        } else {
            let answer;        
            switch (question.questionOrder) {        
                case 2:
                    answer = canvas.template?.answers?.find((q) => q.questionId === 2)?.answerText;
                    if (answer) {
                        returnAnswer += prefix + answer;
                    }
                    break;
                case 3:
                    answer = canvas.template?.answers?.find((q) => q.questionId === 5)?.answerText;
                    if (answer) {
                        returnAnswer += prefix + answer;
                    }
                    break;
                case 4:
                    answer = canvas.template?.answers?.find((q) => q.questionId === 1)?.answerText;
                    if (answer) {
                        returnAnswer += prefix + answer;
                    }
                    break;
                case 5:
                    const answer1 = canvas.template?.answers?.find((q) => q.questionId === 1)?.answerText;
                    const answer2 = canvas.template?.answers?.find((q) => q.questionId === 8)?.answerText;
                    if (answer1 || answer2) {
                        returnAnswer += prefix + (answer1 || '') + (answer1 && answer2 ? "\n\n" : '') + (answer2 || '');
                    }
                    break;
                case 6:
                    answer = canvas.template?.answers?.find((q) => q.questionId === 4)?.answerText;
                    if (answer) {
                        returnAnswer += prefix + answer;
                    }
                    break;
                case 8:
                    answer = canvas.template?.answers?.find((q) => q.questionId === 9)?.answerText;
                    if (answer) {
                        returnAnswer += prefix + answer;
                    }
                    break;
                case 10:
                    const ans1 = canvas.template?.answers?.find((q) => q.questionId === 6)?.answerText;
                    const ans2 = canvas.template?.answers?.find((q) => q.questionId === 5)?.answerText;
                    if (ans1 || ans2) {
                        returnAnswer += prefix + (ans1 || '') + (ans1 && ans2 ? "\n\n" : '') + (ans2 || '');
                    }
                    break;
                default:
                    returnAnswer = "";
                    break;
            }
        }      
    }    
    return returnAnswer;
  }  

  getRecommendation(matchedAnswer): string {
    return matchedAnswer && matchedAnswer.feedback?.length > 0
            ? this.formatRecommendationsAsHtml(JSON.parse(matchedAnswer.feedback[0].commentText))
            : '';    
  }

  nextSection(): void {
    if (!this.selectedSection || this.sections.length === 0) return;  
    const currentIndex = this.sections.indexOf(this.selectedSection);    
    if (this.isFinished()) {
      let dialogRef = this.dialog.open(FlowComponent, {
        height: '380px',
        width: '800px',
      });
    } else {
      const nextIndex = (currentIndex + 1) % this.sections.length;
      this.selectSection(this.sections[nextIndex]);
    }    
  }

  isFinished(): boolean {    
    return this.sections.every(section => section.answer && section.answer.length > 0);    
  }
  
  previousSection(): void {
    if (!this.selectedSection || this.sections.length === 0) return;
  
    const currentIndex = this.sections.indexOf(this.selectedSection);
    const previousIndex = (currentIndex - 1 + this.sections.length) % this.sections.length;
    this.selectSection(this.sections[previousIndex]);
  }
  

  selectSection(section: Section): void {    
    this.selectedSection = section;    
    this.clearRedoUndoStacks();
  }

  onSectionChange(section: Section): void {    
    this.selectedSection = section;    
    this.clearRedoUndoStacks(); // Clear redo stack when a new section is selected
  }

  onInputChange(): void {
    if (this.selectedSection) {
      this.undoStack.push(this.selectedSection.answer);      
      const index = this.sections.findIndex(
        section => section.questionOrder === this.selectedSection?.questionOrder
      );
      if (index !== -1) {
        this.sections[index].answer = this.selectedSection.answer;
      }
      this.debounceSave();
    }
  }

  openSectionOverview(): void {
    const dialogRef = this.dialog.open(SectionOverviewDialogComponent, {
      width: '600px',
      data: {
        sections: this.sections,
        selectedSection: this.selectedSection
      }
    });
  
    dialogRef.afterClosed().subscribe((result: Section | undefined) => {
      if (result) {
        this.selectSection(result);
      }
    });
  }

  debounceSave(): void {
    clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(() => {
      this.saveToBackend();
    }, this.saveTimeoutInMilliS);
  }

  undo(): void {    
    if (this.undoStack.length > 0 && this.selectedSection) {
      this.redoStack.push(this.selectedSection.answer);
      const previousAnswer = this.undoStack.pop();
      if (previousAnswer !== undefined) {
        this.selectedSection.answer = previousAnswer;        
        const index = this.sections.findIndex(
          section => section.questionOrder === this.selectedSection?.questionOrder
        );
        if (index !== -1) {
          this.sections[index].answer = previousAnswer;
        }
        this.debounceSave();
      }
    }
  }

  redo(): void {    
    if (this.redoStack.length > 0 && this.selectedSection) {
      this.undoStack.push(this.selectedSection.answer);
      const nextAnswer = this.redoStack.pop();
      if (nextAnswer !== undefined) {
        this.selectedSection.answer = nextAnswer;        
        const index = this.sections.findIndex(
          section => section.questionOrder === this.selectedSection?.questionOrder
        );
        if (index !== -1) {
          this.sections[index].answer = nextAnswer;
        }
        this.debounceSave();
      }
    }
  }

  clearRedoUndoStacks(): void {    
    this.redoStack = [];
    this.undoStack = [];    
  }

  saveToBackend(): void {
    if (this.selectedSection) {
      this.api.updateAssessmentQuestionnaireAnswer(this.selectedSection.answer, this.questionnaireAssesmentId, this.selectedSection.id).subscribe(
        (ret:any) => {
          this.selectedSection.answerId = ret.answer.id;
          this.lastSaveTime = new Date().toLocaleTimeString();
          this.animate = true;
        },
        (error) => console.error('Save failed', error)
      );
    }
  }

  getRecommendations(): void {    
    // Check for presence of response first:
    if (this.selectedSection.answer.length == 0) {
        this.selectedSection.recommendations = "You input is empty. Please carefully answer the section first."
        
      return;
    }
    // Check if answerId missing (in case of placeholder text from canvas)    
    if (!this.selectedSection.answerId) {
      this.selectedSection.recommendations = "The text from the canvas is for guiding only. Revisit it carefully before asking for recommendations."
      return;
    }

    this.recommendationsLoading = true;    
    if (this.selectedSection) {
      let data: Object;
      // Brief questions, except the first, need the farm_data variable to work, which is given in the answer to the first question.
      if (this.getTemplateId() !== '1' && this.selectedSection.questionOrder !== 1) {        
        data = {
          user_input: this.selectedSection.answer,
          farm_data: this.sections[0].answer
        }      
      } else {
        data = {
          user_input: this.selectedSection.answer
        };
      }
      this.api.getAnswerRecommendation(this.selectedSection.answerId, data).subscribe(
        (response: any) => {          
          // Check if response is a string and parse it if necessary
          let recommendations;
          if (typeof response === 'string') {
            try {
              recommendations = JSON.parse(response);
            } catch (e) {
              console.error('Failed to parse JSON response:', e);
              return;
            }
          } else {
            recommendations = response;
            this.recommendationsLoading = false;
          }  
          // Ensure the response has the expected structure
          if (recommendations && typeof recommendations === 'object' && 'suggestions' in recommendations) {              
            this.selectedSection.recommendations = this.formatRecommendationsAsHtml(recommendations);
          } else {
            console.error('Unexpected response structure:', recommendations);
          }
        },
        (error) => console.error('Recommendation retrieval failed', error)
      );
    }
  }
  
  formatRecommendationsAsHtml(recommendations: { suggestions: Array<{ [key: string]: string }> }): string {    
    console.log(recommendations);
    if (!recommendations || !recommendations.suggestions || recommendations.suggestions.length === 0) {    
      return '<p>No recommendations available.</p>';
    }
  
    let html = '';
    recommendations.suggestions.forEach(suggestion => {
      const title = Object.keys(suggestion)[0];
      const message = suggestion[title];
      html += `<li><strong>${title}:</strong> ${message}</li>`;
    });
    html += '</ul>';
  
    return html;
  }

  onAnimationEnd() {
    this.animate = false;
  }
}